/**
 * CSV export helpers for the rebuilt Historian page (djin-cloud#806, task 18).
 *
 * Deliberately NOT added to `@/utils/chartExport.ts`. That file's
 * `exportToCSV` is shared with `pages/ProjectSummary.tsx` and
 * `pages/AdminNotificationAudit.tsx` (Ruling AM, task-18-supplement.md) — a
 * historian-specific change there would silently alter two unrelated
 * features' exports. Everything below is scoped to this feature only.
 */

import { format } from 'date-fns';
import type { SelectedVariable } from '@/contexts/HistorianContext';

/** Maximum rows for client-side export. */
export const MAX_EXPORT_ROWS = 50000;

/** Warning threshold for row count. */
export const WARN_EXPORT_ROWS = 10000;

/** Rough estimate: ~50 bytes per cell average. */
export function estimateExportSize(rowCount: number, columnCount: number): string {
  const bytes = rowCount * columnCount * 50;
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Same formula-injection guard as `chartExport.ts`'s private
 * `sanitizeCsvCell`. Duplicated rather than imported — that function isn't
 * exported, and Ruling AM forbids touching the shared file to export it.
 * Keep both in lockstep if the escaping rule ever changes.
 */
function sanitizeCsvCellLocal(value: unknown): string {
  if (value === null || value === undefined) return '';
  const str = String(value);
  if (/^[=+\-@|\t\r]/.test(str)) return "'" + str;
  return str;
}

/**
 * A built export row. `timestamp` is always present and always a string
 * (the ISO conversion below) — declared explicitly, rather than left to a
 * generic `Record`, so a row missing its timestamp is a compile error.
 * (It was originally shaped to satisfy `chartExport.ExportDataPoint` without
 * a cast; that no longer applies — this module does not call into
 * `chartExport` at all — but the explicit field earns its place regardless.)
 */
export interface HistorianExportRow {
  timestamp: string;
  [key: string]: string | number | null;
}

/**
 * Builds the export's rows/headers per Ruling AG item 3: both the header
 * KEYS and their display VALUES are the raw wire alias, never
 * `displayLabel` — so a CSV downloaded from the browser and one from
 * `GET /queries/:id?format=csv` are interchangeable on their header row
 * (the server's own column list, `historian-series.service.ts:360`, is
 * `['timestamp', ...spec.series.map(i => i.alias)]`, all lowercase).
 * Aliases are user-chosen labels already; there is no nicer label to lose.
 */
export function buildHistorianExportRows(
  data: Array<Record<string, number | null>>,
  variables: SelectedVariable[]
): { rows: HistorianExportRow[]; headers: Record<string, string> } {
  const headers: Record<string, string> = { timestamp: 'timestamp' };
  for (const v of variables) headers[v.alias] = v.alias;

  const rows: HistorianExportRow[] = data.map((row) => {
    const out: HistorianExportRow = { timestamp: '' };
    for (const key of Object.keys(headers)) {
      if (key === 'timestamp') {
        const ms = row.timestamp;
        out.timestamp = typeof ms === 'number' ? new Date(ms).toISOString() : '';
      } else {
        out[key] = row[key] ?? null;
      }
    }
    return out;
  });

  return { rows, headers };
}

/**
 * Metadata preamble lines (Ruling AM).
 *
 * ⚠️ This comment used to justify the `Timezone:` line by `exportToCSV`'s
 * local-wall-clock reformatting. That rationale is dead: this module no
 * longer delegates to `exportToCSV`, and both branches now emit UTC ISO
 * timestamps, which are unambiguous on their own.
 *
 * The line stays for a different and still-good reason: it records WHICH
 * zone the export was produced in, which is what someone correlating a CSV
 * against a local operations log actually needs. It documents the exporter,
 * not the timestamps.
 */
export function buildMetadataHeaderLines(opts: {
  queryId: string | null;
  start: Date;
  end: Date;
}): string[] {
  return [
    'Historian export',
    `Query ID: ${opts.queryId ?? '(not persisted)'}`,
    `Range: ${opts.start.toISOString()} to ${opts.end.toISOString()}`,
    // "Generated in timezone", not "Timezone": the rows below are UTC ISO, and
    // a bare `Timezone:` four lines above them reads as if it described them.
    `Generated in timezone: ${Intl.DateTimeFormat().resolvedOptions().timeZone}`,
  ];
}

/**
 * Downloads the CSV. Always hand-built — never delegates to
 * `chartExport.exportToCSV`, even when `metadataLines` is empty.
 *
 * ⚠️ Fix round 1 (task-18-review.md H2). The first version of this function
 * delegated to `exportToCSV` exactly when there was no metadata preamble,
 * and that function reformats `timestamp` to LOCAL wall clock with no zone
 * marker (`format(new Date(v), 'yyyy-MM-dd HH:mm:ss')`) — while the
 * hand-built branch (needed for the preamble, which `exportToCSV`'s fixed
 * contract has no hook for) used the ISO string `buildHistorianExportRows`
 * already put in `row.timestamp`. So the two branches disagreed on
 * FORMAT, not just on the preamble's presence — and since the metadata
 * checkbox defaults ON, the DEFAULT export was ISO while unchecking the
 * box silently switched every timestamp to naive local wall-clock, with
 * nothing on screen saying so. Two users comparing exports, or one user
 * comparing their own two exports, could disagree on the same query's
 * numbers — the exact ambiguity the `Timezone:` line exists to remove,
 * and that line was landing on the branch that DIDN'T need it (ISO+Z is
 * already unambiguous) while being absent from the branch that did.
 *
 * The fix is to stop delegating, not to add a second timestamp-format
 * option: both branches now always emit `row.timestamp` (already ISO) as
 * whatever `buildHistorianExportRows` gave it, so toggling the metadata
 * checkbox changes ONLY whether the `#`-prefixed preamble is present.
 * `chartExport.ts` remains untouched — Ruling AM's reason for leaving it
 * alone (`ProjectSummary.tsx` / `AdminNotificationAudit.tsx` depend on its
 * exact local-wall-clock format) still holds; this module just no longer
 * calls it.
 *
 * ⚠️ The preamble still lands BEFORE the header row, so the file's first
 * line is `# Historian export` whenever `metadataLines` is non-empty —
 * Task 19's E2E must tolerate that rather than assume `/^timestamp,/`
 * unconditionally (recorded in task-18-report.md for that task's brief).
 */
export function downloadHistorianCsv(
  rows: HistorianExportRow[],
  filename: string,
  headers: Record<string, string>,
  metadataLines: string[] = []
): void {
  // Headers get the SAME sanitize + quote treatment as cells. They were
  // previously raw, which was an asymmetry rather than a considered choice:
  // every data cell is guarded and quoted, the header line was neither.
  //
  // Today's only caller passes aliases, and `sanitizeAlias`'s charset
  // (`[A-Za-z0-9_.-]`) already neutralises the dangerous payloads — `=cmd|…`,
  // `@SUM(…)`, `+…` and tab-prefixed all become `_`. A leading `-` is the one
  // formula character that survives, and it coerces to arithmetic rather than
  // executing. So this is defence in depth, not a live exploit.
  //
  // It is worth having anyway: `headers` is a plain `Record<string, string>`
  // on an exported function, so nothing stops a future caller passing a raw
  // device label or user string — and an unquoted header containing a comma
  // silently breaks the file's column structure.
  const headerRow = Object.values(headers)
    .map((h) => {
      const sanitized = sanitizeCsvCellLocal(h);
      return /[",\n\r]/.test(sanitized) ? `"${sanitized.replace(/"/g, '""')}"` : sanitized;
    })
    .join(',');
  const dataRows = rows.map((row) =>
    Object.keys(headers)
      .map((key) => {
        const sanitized = sanitizeCsvCellLocal(row[key]);
        return /[",\n\r]/.test(sanitized) ? `"${sanitized.replace(/"/g, '""')}"` : sanitized;
      })
      .join(',')
  );
  const body = [headerRow, ...dataRows].join('\n');
  const csv =
    metadataLines.length > 0
      ? [metadataLines.map((l) => `# ${l}`).join('\n'), '', body].join('\n')
      : body;

  const blob = new Blob([csv], { type: 'text/csv' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${filename}_${format(new Date(), 'yyyy-MM-dd_HHmm')}.csv`;
  link.click();
  window.URL.revokeObjectURL(url);
}
